import { Theme } from "@mui/material";
import { GraphicComponentOption } from "echarts";
import { QuadrantLabel } from "./types";

type QuadrantLabelPosition = {
  left?: string;
  right?: string;
  top?: string;
  bottom?: string;
};

/**
 * Converts semantic position to percentage-based positioning.
 */
const getPositionValues = (position: QuadrantLabel["position"]): QuadrantLabelPosition => {
  const top = "9%";
  const bottom = "18%";
  const left = "5.5%";
  const right = "2.5%";

  const positionMap: Record<QuadrantLabel["position"], QuadrantLabelPosition> = {
    "top-left": { top, left },
    "top-right": { top, right },
    "bottom-left": { bottom, left },
    "bottom-right": { bottom, right },
  };
  return positionMap[position];
};

/**
 * Creates the background rectangle for a quadrant label.
 */
const createBackgroundRect = (theme: Theme) => ({
  type: "rect",
  shape: {
    width: 164,
    height: 63,
    r: 4,
    padding: 8,
  },
  style: {
    fill: theme.palette.mode === "dark" ? "#F6F1FF1A" : "#F6F1FF80",
  },
});

/**
 * Creates the text content for a quadrant label.
 */
const createLabelText = (
  title: string,
  titleColor: string,
  descriptions: string[],
  theme: Theme
) => ({
  type: "group",
  children: [
    {
      type: "text",
      left: 12,
      top: 8,
      style: {
        text: title,
        fontSize: 12,
        fontWeight: 600,
        fill: titleColor,
      },
    },
    {
      type: "text",
      left: 12,
      top: 28,
      style: {
        text: descriptions.join("\n"),
        fontSize: 12,
        fontWeight: 200,
        fill: theme.palette.text.primary,
      },
    },
  ],
});

/**
 * Creates a single quadrant label graphic component.
 */
const createQuadrantLabel = (config: QuadrantLabel, theme: Theme): GraphicComponentOption => {
  const position = getPositionValues(config.position);
  return {
    type: "group",
    ...position,
    children: [
      createBackgroundRect(theme),
      createLabelText(config.title, config.titleColor, config.descriptions, theme),
    ],
  } as GraphicComponentOption;
};

/**
 * Converts an array of quadrant label configs into ECharts graphic components.
 */
export const quadrantLabelsToGraphics = (
  configs: QuadrantLabel[],
  theme: Theme
): GraphicComponentOption[] => {
  return configs.map((config) => createQuadrantLabel(config, theme));
};
